import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { acquireOverlayBodyLock, nextOverlayVisualLayer } from '../shared/overlay-lock';
import { useOverlayHistoryLayer } from '../shared/overlay-history';
import { UiIcon } from './ActionIcon';

export function AdaptiveSheet({ open, eyebrow, title, onDismiss, children, footer, closeLabel = 'Close' }: {
  open: boolean;
  eyebrow?: ReactNode;
  title: ReactNode;
  onDismiss: () => void;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
}) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const dismiss = useOverlayHistoryLayer(open, onDismiss);
  const [visualViewport, setVisualViewport] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [visualLayer, setVisualLayer] = useState(0);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const release = acquireOverlayBodyLock();
    setVisualLayer(nextOverlayVisualLayer());
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      release();
      previousFocus?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const viewport = window.visualViewport;
    if (!viewport) {
      setVisualViewport(null);
      return;
    }
    const sync = () => setVisualViewport({
      top: viewport.offsetTop,
      left: viewport.offsetLeft,
      width: viewport.width,
      height: viewport.height
    });
    sync();
    viewport.addEventListener('resize', sync);
    viewport.addEventListener('scroll', sync);
    return () => {
      viewport.removeEventListener('resize', sync);
      viewport.removeEventListener('scroll', sync);
    };
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dismiss, open]);

  if (!open || typeof document === 'undefined') return null;
  const viewportStyle = {
    ...(visualViewport ? {
      '--ux-vv-top': `${visualViewport.top}px`,
      '--ux-vv-left': `${visualViewport.left}px`,
      '--ux-vv-width': `${visualViewport.width}px`,
      '--ux-vv-height': `${visualViewport.height}px`
    } : {}),
    zIndex: 100 + visualLayer
  } as CSSProperties;

  return createPortal(<div className="ux-sheet-backdrop" style={viewportStyle} role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) dismiss();
  }}>
    <section className="ux-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <header className="ux-sheet-head">
        <div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2 id={titleId}>{title}</h2></div>
        <button ref={closeRef} type="button" className="ux-sheet-close" aria-label={closeLabel} title={closeLabel} onClick={dismiss}><UiIcon name="close" size={20} /></button>
      </header>
      <div className="ux-sheet-body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </section>
  </div>, document.body);
}
